import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { CacheProvider } from "@emotion/react";

import { SmartClaimProvider } from "./context/SmartClaimContext";
import { UserProvider } from "./context/userContext";
import { TrueClaimProvider } from "./context/TrueClaimContext";
import { AuthProvider } from "./context/AuthContext";
import {
  SettingsProvider,
  SettingsConsumer,
} from "./@core/context/settingsContext";
import ThemeComponent from "./@core/theme/ThemeComponent";

import { createEmotionCache } from "./@core/utils/create-emotion-cache";

import UserLayout from "./layouts/UserLayout";
import AclGuard from "./@core/components/auth/AclGuard";
import AuthGuard from "./@core/components/auth/AuthGuard";
import Spinner from "./@core/components/spinner";
import SnackbarAlert from "./component/ui/toast/SnackbarAlert";
import { useAuth } from "./hooks/useAuth";

import "react-perfect-scrollbar/dist/css/styles.css";
import { UploadProvider } from "./context/uploadContext";
import Dashboard from "./pages/dashboard";
import { DashboardProvider } from "./context/Dashboard";
import UsedReturn from "./pages/used-return";
import Upload_excel from "./pages/upload-excel";

const LoginPage = lazy(() => import("./pages/login"));
const CustomerReturn = lazy(() => import("./pages/customer-return"));
const WrongRto = lazy(() => import("./pages/wrong-rto"));
const NonBarcodeRto = lazy(() => import("./pages/non-barcode-rto"));
const Damage = lazy(() => import("./pages/damage"));
const MissingQty = lazy(() => import("./pages/missing-qty"));
const OtpLess = lazy(() => import("./pages/otp-less"));
const Payment = lazy(() => import("./pages/payment"));
const Return = lazy(() => import("./pages/returns"));
const Report = lazy(() => import("./pages/report"));
const Lost = lazy(() => import("./pages/lost"));
const Upload = lazy(() => import("./pages/upload"));
// import 'src/iconify-bundle/icons-bundle-react'
const clientSideEmotionCache = createEmotionCache();

const ProtectedRoute = ({ element }) => {
  const auth = useAuth();

  if (!auth.user || !localStorage.getItem("userData")) {
    return <LoginPage />;
  }
  return (
    <AclGuard
      aclAbilities={{ action: "manage", subject: "all" }}
      guestGuard={true}
      authGuard={true}
    >
      {element}
    </AclGuard>
  );
};

const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const userData = localStorage.getItem("userData");

  useEffect(() => {
    if (!userData) {
      navigate("/login");
    } else if (location.pathname === "/") {
      navigate("/customer-return");
    }
  }, [userData, location.pathname, navigate]);

  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            auth.user ? (
              <AuthGuard>
                <UserLayout>
                  <Suspense fallback={<Spinner />}>
                    <Routes>
                      <Route
                        path="/customer-return"
                        element={<ProtectedRoute element={<CustomerReturn />} />}
                      />
                      <Route
                        path="/wrong-rto"
                        element={<ProtectedRoute element={<WrongRto />} />}
                      />
                      <Route
                        path="/non-barcode-rto"
                        element={<ProtectedRoute element={<NonBarcodeRto />} />}
                      />
                      <Route
                        path="/damage"
                        element={<ProtectedRoute element={<Damage />} />}
                      />
                      <Route
                        path="/missing-qty"
                        element={<ProtectedRoute element={<MissingQty />} />}
                      />
                      <Route
                        path="/used"
                        element={<ProtectedRoute element={<UsedReturn />} />}
                      />
                      <Route
                        path="/otp-less"
                        element={<ProtectedRoute element={<OtpLess />} />}
                      />
                      <Route
                        path="/payment"
                        element={<ProtectedRoute element={<Payment />} />}
                      />
                      <Route
                        path="/returns"
                        element={<ProtectedRoute element={<Return />} />}
                      />
                      <Route
                        path="/lost"
                        element={<ProtectedRoute element={<Lost />} />}
                      />
                      <Route
                        path="/upload-excel"
                        element={<ProtectedRoute element={<Upload_excel />} />}
                      />
                      <Route
                        path="/upload"
                        element={<ProtectedRoute element={<Upload />} />}
                      />
                      <Route
                        path="/report"
                        element={<ProtectedRoute element={<Report />} />}
                      />
                      <Route
                        path="/dashboard"
                        element={<ProtectedRoute element={<Dashboard />} />}
                      />
                    </Routes>
                  </Suspense>
                </UserLayout>
              </AuthGuard>
            ) : (
              <LoginPage />
            )
          }
        />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  return (
    <Router>
      <CacheProvider value={clientSideEmotionCache}>
        <AuthProvider>
          <UserProvider>
            <DashboardProvider>
              <SmartClaimProvider>
                <TrueClaimProvider>
                  <UploadProvider>
                    <SettingsProvider>
                      <SettingsConsumer>
                        {({ settings }) => (
                          <ThemeComponent settings={settings}>
                            {/* <RouteChangeLoader> */}
                            <SnackbarAlert />
                            <AppRoutes />
                            {/* </RouteChangeLoader> */}
                          </ThemeComponent>
                        )}
                      </SettingsConsumer>
                    </SettingsProvider>
                  </UploadProvider>
                </TrueClaimProvider>
              </SmartClaimProvider>
            </DashboardProvider>
          </UserProvider>
        </AuthProvider>
      </CacheProvider>
    </Router>
  );
};

export default App;
